import { useState } from "react";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, MapPin, CheckCircle, Loader2 } from "lucide-react";

const FORMSPREE_URL = "https://formspree.io/f/mvzvankb";

const typesTravaux = [
  "Rénovation complète",
  "Electricité",
  "Plomberie",
  "Carrelage",
  "Menuiserie",
  "Peinture",
  "Maçonnerie",
  "Autre",
];

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    type_travaux: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.nom.trim()) errs.nom = "Ce champ est obligatoire";
    if (!form.telephone.trim()) errs.telephone = "Ce champ est obligatoire";
    if (!form.email.trim()) errs.email = "Ce champ est obligatoire";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Email invalide";
    if (!form.type_travaux) errs.type_travaux = "Sélectionnez un type";
    if (!form.message.trim()) errs.message = "Ce champ est obligatoire";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          nom: form.nom,
          telephone: form.telephone,
          email: form.email,
          type_travaux: form.type_travaux,
          message: form.message,
        }),
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ nom: "", telephone: "", email: "", type_travaux: "", message: "" });
      } else {
        setErrors({ submit: "Une erreur est survenue. Veuillez réessayer." });
      }
    } catch {
      setErrors({ submit: "Une erreur est survenue. Veuillez réessayer." });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <>
      <SEO
        title="Devis gratuit rénovation Paris — Contactez Bati Rui"
        description="Demandez votre devis gratuit pour travaux de rénovation à Paris et Ile-de-France. Réponse sous 48h. Bati Rui, 45 rue de l'Espérance 75013 Paris. Tél : 06 32 25 52 75."
        canonical="https://www.batirui.com/contact"
      />

      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F8F6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            data-testid="contact-h1"
            className="font-heading text-4xl sm:text-5xl font-black text-[#1B2E4B] tracking-tight"
          >
            Demandez votre devis gratuit
          </h1>
          <p className="mt-4 text-[#3D3D3A] text-base sm:text-lg leading-relaxed max-w-3xl">
            Vous avez un projet de rénovation ou d'aménagement ? Remplissez le
            formulaire ci-dessous ou contactez-nous directement. Nous vous répondons
            sous 48h et nous déplaçons pour un rendez-vous de chiffrage sans engagement.
          </p>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              {success ? (
                <div
                  data-testid="contact-success"
                  className="bg-green-50 border border-green-200 rounded-md p-8 text-center"
                >
                  <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#1B2E4B] mb-2">
                    Demande envoyée !
                  </h3>
                  <p className="text-[#3D3D3A]">
                    Nous avons bien reçu votre demande et vous répondrons sous 48h.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  noValidate
                >
                  {/* Nom */}
                  <div>
                    <Label htmlFor="nom" className="text-[#3D3D3A] font-medium text-sm">
                      Prénom et Nom *
                    </Label>
                    <Input
                      id="nom"
                      data-testid="input-nom"
                      value={form.nom}
                      onChange={(e) => updateField("nom", e.target.value)}
                      className={`mt-1.5 bg-white border-[#EEECE8] focus:border-[#1B2E4B] focus:ring-[#1B2E4B] ${
                        errors.nom ? "border-[#C0392B]" : ""
                      }`}
                      placeholder="Jean Dupont"
                    />
                    {errors.nom && (
                      <p className="text-[#C0392B] text-xs mt-1">{errors.nom}</p>
                    )}
                  </div>

                  {/* Telephone */}
                  <div>
                    <Label htmlFor="telephone" className="text-[#3D3D3A] font-medium text-sm">
                      Téléphone *
                    </Label>
                    <Input
                      id="telephone"
                      data-testid="input-telephone"
                      type="tel"
                      value={form.telephone}
                      onChange={(e) => updateField("telephone", e.target.value)}
                      className={`mt-1.5 bg-white border-[#EEECE8] focus:border-[#1B2E4B] focus:ring-[#1B2E4B] ${
                        errors.telephone ? "border-[#C0392B]" : ""
                      }`}
                      placeholder="06 12 34 56 78"
                    />
                    {errors.telephone && (
                      <p className="text-[#C0392B] text-xs mt-1">{errors.telephone}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-[#3D3D3A] font-medium text-sm">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={`mt-1.5 bg-white border-[#EEECE8] focus:border-[#1B2E4B] focus:ring-[#1B2E4B] ${
                        errors.email ? "border-[#C0392B]" : ""
                      }`}
                      placeholder="jean.dupont@email.com"
                    />
                    {errors.email && (
                      <p className="text-[#C0392B] text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Type de travaux */}
                  <div>
                    <Label className="text-[#3D3D3A] font-medium text-sm">
                      Type de travaux *
                    </Label>
                    <Select
                      value={form.type_travaux}
                      onValueChange={(val) => updateField("type_travaux", val)}
                    >
                      <SelectTrigger
                        data-testid="select-type-travaux"
                        className={`mt-1.5 bg-white border-[#EEECE8] focus:border-[#1B2E4B] focus:ring-[#1B2E4B] ${
                          errors.type_travaux ? "border-[#C0392B]" : ""
                        }`}
                      >
                        <SelectValue placeholder="Sélectionnez un type de travaux" />
                      </SelectTrigger>
                      <SelectContent>
                        {typesTravaux.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.type_travaux && (
                      <p className="text-[#C0392B] text-xs mt-1">{errors.type_travaux}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-[#3D3D3A] font-medium text-sm">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={form.message}
                      onChange={(e) => updateField("message", e.target.value)}
                      rows={5}
                      className={`mt-1.5 bg-white border-[#EEECE8] focus:border-[#1B2E4B] focus:ring-[#1B2E4B] ${
                        errors.message ? "border-[#C0392B]" : ""
                      }`}
                      placeholder="Décrivez votre projet de rénovation..."
                    />
                    {errors.message && (
                      <p className="text-[#C0392B] text-xs mt-1">{errors.message}</p>
                    )}
                  </div>

                  {errors.submit && (
                    <p className="text-[#C0392B] text-sm">{errors.submit}</p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={loading}
                    className="w-full bg-[#C0392B] hover:bg-[#a02f23] disabled:opacity-60 text-white rounded-md px-6 py-3.5 text-base font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    Envoyer ma demande
                  </button>
                  <p className="text-xs text-[#3D3D3A]/60">
                    Vos données sont utilisées uniquement pour répondre à votre
                    demande, conformément au RGPD.
                  </p>
                </form>
              )}
            </div>

            {/* Coordinates + Map */}
            <div className="lg:col-span-2 space-y-8">
              <div data-testid="contact-info">
                <h2 className="font-heading text-xl font-semibold text-[#1B2E4B] mb-4">
                  Nos coordonnées
                </h2>
                <div className="space-y-4 text-[#3D3D3A]">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="mt-0.5 text-[#C0392B] flex-shrink-0" />
                    <div>
                      <p className="font-medium">Bati Rui</p>
                      <p>45 rue de l'Espérance</p>
                      <p>75013 Paris</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-[#C0392B] flex-shrink-0" />
                    <a
                      href="tel:+33632255275"
                      className="font-medium hover:text-[#C0392B] transition-colors"
                    >
                      06 32 25 52 75
                    </a>
                  </div>
                  <p className="text-sm text-[#3D3D3A]/70 pt-2">
                    Zone d'intervention : Paris & Ile-de-France
                  </p>
                </div>
              </div>

              {/* Google Maps */}
              <div data-testid="google-maps" className="rounded-md overflow-hidden">
                <iframe
                  title="Localisation Bati Rui - Paris 13e"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2626.6!2d2.3524!3d48.8234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e671b4e7b2e5e7%3A0x0!2s45+Rue+de+l'Esp%C3%A9rance%2C+75013+Paris!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
